import Axios from 'axios'
import debouncePromise from 'debounce-promise'
import _defaults from 'lodash/defaults'
import _each from 'lodash/each'
import _filter from 'lodash/filter'
import _find from 'lodash/find'
import _isArray from 'lodash/isArray'
import _isEqual from 'lodash/isEqual'
import _isFunction from 'lodash/isFunction'
import _keys from 'lodash/keys'
import _map from 'lodash/map'

import { modelsDiffer } from '../_helpers/value'
import { set } from './_storeMethods.js'

const $axios = Axios.create({
  baseURL: '/'
})

/*
|--------------------------------------------------------------------------
| UPDATE ACTION
|--------------------------------------------------------------------------
*/

const updateAction = (ctx, payload) => {
  return new Promise((resolve, reject) => {
    $axios.put(`${ctx.state.apiBaseUrl}/${payload.id}`, payload).then(
      res => {
        if (res.status === 200) {
          if (
            !ctx.state.lastUpdatePayload ||
            modelsDiffer(res.data, ctx.state.lastUpdatePayload, ctx.state.silentAttributes || [])
          ) {
            ctx.commit('setOne', res.data)
          }
          resolve(res.data)
        } else if (ctx.state.rejectUpdateErrors) {
          reject(res)
        } else {
          resolve()
        }
      },
      error => {
        console.error(error)
        if (ctx.state.rejectUpdateErrors) {
          reject(error)
        } else {
          resolve()
        }
      }
    )
  })
}
const updateActionDebounced = debouncePromise(updateAction, 250)

/**
 * SEARCH
 *
 * This method will query backend entities.
 * The `query` should be the array of arguments like ['where', 'user_id'. '=', 1].
 *
 * @param {Object} ctx
 * @param {Object} payload  The payload could be the `query` array or {query: [], options: {bypassCache: false}}
 * @returns {Promise} A `Promise` that resolves to the results collection.
 */

const search = (ctx, payload) => {
  //  SIMPLE USAGE
  if (!payload.query) {
    payload = {
      query: payload
    }
  }

  // OPTIONS
  // let options = _defaults(
  //     _get(payload, 'options', {}),
  //     {
  //         bypassCache: false
  //     }
  // );

  if (
    // eslint-disable-next-line no-constant-condition
    true //|| /* @todo cache is not working with promise */ options.bypassCache
    //|| !isQueryCached(ctx, 'search', queryArray)
  ) {
    // console.log(payload);

    let promise = new Promise((resolve, reject) => {
      $axios
        .get(getApiBaseUrl(ctx), {
          params: {
            query: JSON.stringify(payload.query || []),
            with: payload.with,
            include: payload.include
          }
        })
        .then(
          res => {
            if ([200].includes(res.status)) {
              ctx.commit('setMany', res.data)
              resolve(res.data)
            }
          },
          error => {
            // resolve();
            reject(error)
          }
        )
    })

    cacheQuery(ctx, 'search', payload.query, promise)

    return promise
  } else {
    return getQueryCache(ctx, 'search', payload)
  }
}

/*
|--------------------------------------------------------------------------
| FETCH METHOD
|--------------------------------------------------------------------------
*/
const fetch = context => {
  if (!isQueryCached(context, 'all')) {
    cacheQuery(context, 'all')
    return new Promise(resolve => {
      $axios.get(getApiBaseUrl(context)).then(
        res => {
          if (res.status === 200) {
            context.commit('set', {
              field: 'collection',
              value: res.data
            })
            resolve(res.data)
          }
        },
        () => {
          resolve()
          //reject(error);
        }
      )
    })
  }
}

let StoreCollection = {
  namespaced: true,
  state: {
    model: null,
    apiBaseUrl: undefined,
    collection: [],
    silentAttributes: [],

    lastUpdatePayload: null,
    cache: {}
  },
  mutations: {
    set,
    setMany(state, payload) {
      if (payload && payload.length) {
        let collection = state.collection
        _each(payload, entry => {
          collection = collectionWithoutItem(collection, entry)
          if (entry && entry.id) {
            collection.push(
              state.model ? new state.model({ propsData: { attributes: entry } }) : entry
            )
          }
        })
        state.collection = collection
        return state.collection
      }
    },
    setOne(state, payload) {
      if (payload && payload.id) {
        let collection = collectionWithoutItem(state.collection, payload)
        let model = state.model ? new state.model({ data: { attributes: payload } }) : payload
        collection.push(model)
        state.collection = collection
        return state.collection
      }
    },
    removeOne(state, payload) {
      if (payload && payload.id) {
        state.collection = collectionWithoutItem(state.collection, payload)
        return state.collection
      }
    }
  },
  actions: {
    /*
        |--------------------------------------------------------------------------
        | BOOT
        |--------------------------------------------------------------------------
        */
    boot(context) {
      if (!context.state.collection || !context.state.collection.length) {
        return fetch(context)
      }
    },
    bootFor(context, payload) {
      return new Promise(resolve => {
        if (!context.state.collection.length) {
          resolve(search(context, payload))
        } else {
          resolve(context.state.collection)
        }
      })
    },

    /**
     * FETCH ONE
     *
     * Fetch a resource by it's `id`.
     * The `query` should be an object containing an id attribute, such as {id: 1}.
     *
     * @param {Object} ctx
     * @param {Object} payload  The payload could be the `query` object or
     *                          {query: {id: 1}, options: {bypassCache: false}}
     *
     * @returns {Promise} A `Promise` that resolves to the resulting resource.
     */

    fetch,
    fetchOne(context, payload) {
      return new Promise((Resolve, Reject) => {
        let options = {
          bypassCache: false
        }

        // support for advanced usage
        if (payload.query) {
          options = _defaults(payload.options || {}, options)
          payload = payload.query
        }

        if (options.bypassCache || !isQueryCached(context, 'fetchOne', payload)) {
          cacheQuery(context, 'fetchOne', payload)
          new Promise(() => {
            let url = `${getApiBaseUrl(context)}/${payload.id}`

            // add relevant options as params
            let params = {}

            if (options.with) {
              params['with'] = options.with
            }

            if (options.append) {
              params['append'] = options.append
            }

            let paramString = _map(_keys(params), k => {
              return `${k}=${params[k]}`
            }).join('&')

            $axios.get(`${url}?${paramString}`).then(
              res => {
                if ([200].includes(res.status)) {
                  context.commit('setOne', res.data)
                  Resolve(res.data)
                }
              },
              error => {
                Reject(error)
              }
            )
          })
        } else {
          Resolve(getQueryCache(context, 'fetchOne', payload))
        }
      })
    },
    search,

    /**
     * CREATE
     *
     * @param ctx
     * @param payload
     * @returns {Promise<any>}
     */

    create(ctx, payload) {
      return new Promise(resolve => {
        $axios.post(getApiBaseUrl(ctx), payload).then(
          res => {
            if ([200, 201].includes(res.status)) {
              ctx.commit('setOne', { resource: res.data })
              resolve(res.data)
            }
          },
          error => {
            console.error(
              'An error occurred while creating',
              payload,
              'at',
              getApiBaseUrl(ctx),
              '\n\nError:',
              error
            )
            resolve(null)
          }
        )
      })
    },

    /**
     * UPDATE
     *
     * @param ctx
     * @param payload
     * @returns {*}
     */

    update(ctx, payload) {
      let model = payload.model || payload
      model = ctx.state.model ? model.toObject() : model
      let opts = _defaults(payload.options || {}, {
        debounce: true
      })

      if (!model) {
        throw 'StoreModel::update got no Model (payload.model).'
      } else {
        if (
          !ctx.state.lastUpdatePayload ||
          modelsDiffer({ ...ctx.state.lastUpdatePayload }, model)
        ) {
          ctx.commit('setOne', model)
          ctx.commit('set', {
            field: 'lastUpdatePayload',
            value: { ...model }
          })
          return opts.debounce ? updateActionDebounced(ctx, model) : updateAction(ctx, model)
        } else {
          return Promise.resolve(model)
        }
      }
    },

    /**
     * DELETE
     *
     * @param ctx
     * @param payload
     * @returns {Promise<any>}
     */
    delete(ctx, payload) {
      let model = payload.model || { id: payload.id || payload }
      let query = payload.query || null
      if (model.id) {
        ctx.commit('removeOne', model)
        return new Promise(resolve => {
          let requestConfig = { params: query ? { query: JSON.stringify(query) } : {} }
          let requestUrl = `${ctx.state.apiBaseUrl}/${model.id}`
          $axios.delete(requestUrl, requestConfig).then(
            res => {
              if (res.status !== 204) {
                // @todo on error, rollback collection change by previously storing a temporary copy of the original
                resolve(ctx.state.collection)
              } else {
                resolve(ctx.state.collection)
              }
            },
            error => {
              console.error(error)
              resolve()
              //reject(error);
            }
          )
        })
      }
    }
  },
  getters: {
    find: state => {
      return query => {
        // @todo let entryObject = state.model ? entry.toObject() : entry;
        return _find(state.collection, query)
      }
    },
    forQuery: state => {
      return query => {
        return _filter(state.collection, entry => {
          return searchPredicate(entry, query)
        })
      }
    }
  }
}

/*
|--------------------------------------------------------------------------
| QUERY CACHE
|--------------------------------------------------------------------------
*/

const cacheDurationSeconds = 60

function isQueryCached(ctx, type, params = {}) {
  let key = JSON.stringify(params)
  return (
    ctx.state.cache[type] &&
    ctx.state.cache[type][key] &&
    ctx.state.cache[type][key]['time'] + cacheDurationSeconds * 1000 >= Date.now()
  )
}

function getQueryCache(ctx, type, params = {}) {
  let cache = { ...ctx.state.cache }
  let key = JSON.stringify(params)
  return cache[type] && cache[type][key] && cache[type][key]['result']
    ? cache[type][key]['result']
    : null
}

function cacheQuery(ctx, type, params = {}, result) {
  let cache = { ...ctx.state.cache }
  cache[type] = cache[type] || {}
  cache[type][JSON.stringify(params)] = {
    time: Date.now(),
    result // @todo this cold be improved once we have a method that converts api search query to JS collection filter
  }
  ctx.commit('set', { field: 'cache', value: cache })
}

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

function getApiBaseUrl(ctx) {
  if (!ctx.state.apiBaseUrl) {
    throw 'No Api Base Url set.'
  } else {
    if (_isFunction(ctx.state.apiBaseUrl)) {
      return ctx.state.apiBaseUrl.call(ctx)
    } else {
      return ctx.state.apiBaseUrl
    }
  }
}

function collectionWithoutItem(collection, item) {
  return (
    _filter(collection || [], entry => {
      return entry.id !== item.id
    }) || []
  )
}

function searchPredicate(entry, query) {
  let valid = true
  _each(_keys(query), key => {
    // falsey collection entries
    if (!entry) {
      valid = false
      return false
    }

    // arrays
    if (_isArray(query[key])) {
      if (!query[key].includes(entry[key])) {
        valid = false
        return false
      }
    }

    // simple
    else if (!_isEqual(entry[key], query[key])) {
      valid = false
      return false
    }
  })
  return valid
}

/*
|--------------------------------------------------------------------------
| EXPORTS
|--------------------------------------------------------------------------
*/

export default StoreCollection

export { $axios, StoreCollection }
